
import React, { useState } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { sendToSalesforce } from '@/utils/salesforce';
import ContactDetails from './contact/ContactDetails';
import ContactFormInputs from './contact/ContactFormInputs';

const ContactForm: React.FC = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    zipCode: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSent, setFormSent] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) {
      console.log('Form is already submitting, preventing duplicate submission');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      console.log('Submitting form data:', formData);
      
      // Create a formatted object with form data
      const formSubmissionData = {
        ...formData,
        name: `${formData.firstName} ${formData.lastName}`
      };
      
      const result = await sendToSalesforce(formSubmissionData, 'contact');
      console.log('Form submission result:', result);
      
      // Display success message, but check for warnings
      if (result.warning) {
        toast({
          title: "Message Received",
          description: result.message + " " + result.warning,
          variant: "default",
        });
      } else {
        toast({
          title: "Message sent!",
          description: "Your message has been sent to our team and we'll get back to you as soon as possible.",
        });
      }
      
      // Set form as sent to show a success state
      setFormSent(true);
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        zipCode: '',
        message: '',
      });
      
    } catch (error) {
      console.error('Error submitting form:', error);
      
      // More descriptive error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: "Error",
        description: `There was a problem sending your message: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-white">
      <div className="container-wide">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <ContactDetails />
          
          {formSent ? (
            <div className="bg-green-50 p-8 rounded-lg border border-green-100 flex flex-col items-center justify-center">
              <div className="rounded-full bg-green-100 p-4 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-green-800 mb-2">Thank You!</h3>
              <p className="text-green-700 text-center mb-4">Your message has been received. We'll get back to you shortly.</p>
              <button 
                onClick={() => setFormSent(false)} 
                className="text-green-600 hover:text-green-800 font-medium">
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <ContactFormInputs
                formData={formData}
                handleChange={handleChange}
                isSubmitting={isSubmitting}
              />
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
